import React, { useMemo, useState, useEffect } from "react";
import api from "./utils/api";
import { Routes, Route, useLocation } from "react-router-dom";
import BackendTest from "./BackendTest";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Dashboard from "./pages/Dashboard";
import AddTransaction from "./pages/AddTransaction";
import AddTransactionModal from "./components/AddTransactionModal";
import ReceiptsPage from "./components/ReceiptsPage"; // ✅ Receipts page
import SavedCards from "./components/SavedCards"; // ✅ Saved Cards page
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useRef } from "react";


import "./styles/global.css";
import "./styles/dashboard.css";
import "./styles/cards.css";
import "./styles/transactions.css";
import "./styles/charts.css";
import "./styles/receipts.css";
import "./styles/savedcards.css";
import "./styles/chatbot.css";

export default function App() {
  const hasShownRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hey! I'm your AI finance assistant 💬. Ask me about your spending, savings, or budgets.",
    },
  ]);
  const [userInput, setUserInput] = useState("");
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const location = useLocation();

  /// AI Part starts ///
  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const newMessage = { text: userInput, sender: "user" };
    setMessages((prev) => [...prev, newMessage]);

    const input = userInput.toLowerCase();
    let botReply = "";

    // Fetch latest transactions
    let storedTransactions = [];
    try {
      const res = await api.get("/transactions");
      storedTransactions = res.data || [];
    } catch (error) {
      console.warn("Error fetching transactions:", error);
    }

    // Prepare data
    const categories = [
      ...new Set(storedTransactions.map((t) => (t.category || "").toLowerCase())),
    ];
    const items = storedTransactions.map((t) => (t.title || "").toLowerCase());

    // Basic logic
    if (input.includes("hello") || input.includes("hi")) {
      botReply =
        "Hey there! 👋 How can I assist you with your finances today?";
    } else if (input.includes("income")) {
      const income = storedTransactions
        .filter((t) => t.type === "income")
        .reduce((a, b) => a + b.amount, 0);
      botReply = `Your total income so far is ₹${income}. 💼`;
    } else if (input.includes("expense") || input.includes("spend")) {
      const expenses = storedTransactions
        .filter((t) => t.type === "expense")
        .reduce((a, b) => a + b.amount, 0);
      botReply = `You've spent a total of ₹${expenses} so far. 🧾`;
    } else if (input.includes("savings") || input.includes("save")) {
      const income = storedTransactions
        .filter((t) => t.type === "income")
        .reduce((a, b) => a + b.amount, 0);
      const expenses = storedTransactions
        .filter((t) => t.type === "expense")
        .reduce((a, b) => a + b.amount, 0);
      const savings = income - expenses;
      botReply = `Your current savings balance is ₹${
        savings >= 0 ? savings : 0
      }. 💰`;
    } else if (input.includes("report") || input.includes("analytics")) {
      botReply =
        "You can view detailed spending analytics in the Reports or Dashboard section 📊.";
    } else if (input.includes("help")) {
      botReply =
        "Try asking:\n• How much did I spend on food?\n• What’s my total shopping expense?\n• What’s the price of coffee?\n• Show my savings.";
    } else {
      // Category and item logic
      const matchedCategory = categories.find((cat) => input.includes(cat));
      const matchedItem = items.find((it) => input.includes(it));

      if (matchedCategory) {
        const total = storedTransactions
          .filter(
            (t) => (t.category || "").toLowerCase() === matchedCategory
          )
          .reduce((sum, t) => sum + Number(t.amount), 0);
        botReply = `You’ve spent ₹${total} on ${matchedCategory} so far. 🧾`;
      } else if (matchedItem) {
        const match = storedTransactions.find(
          (t) => (t.title || "").toLowerCase() === matchedItem
        );
        botReply = match
          ? `The price of "${match.title}" was ₹${match.amount}.`
          : "Hmm, I couldn’t find that item in your transactions.";
      } else {
        botReply =
          "Sorry, I couldn’t analyze that 🤔. Try asking about income, expenses, or a specific category like 'food' or 'travel'.";
      }
    }

    // Simulate bot delay
    setTimeout(() => {
      setMessages((prev) => [...prev, { text: botReply, sender: "bot" }]);
    }, 600);

    setUserInput("");
  };
  //// AI Part ends //////

  // Fetch transactions from backend on mount
  useEffect(() => {
    api
      .get("/transactions")
      .then((res) => {
        const mapped = res.data.map((t) => ({
          ...t,
          id: t._id || t.id,
          title: t.title || t.description || "Untitled Transaction",
        }));
        setTransactions(mapped);
      })
      .catch((err) => console.error("Failed to fetch transactions", err));
  }, []);

  // Add transaction via backend
  const addTransaction = async (payload) => {
    // ✅ ADD THIS BLOCK
    if (!payload.title || !payload.amount) {
      toast.error("Title and amount are required");
      return;
    }
  
    if (Number(payload.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/transactions", payload);
  
      const newTx = {
        ...res.data,
        id: res.data._id || res.data.id,
        title: res.data.title || res.data.description || "Untitled Transaction",
      };
  
      setTransactions((prev) => [newTx, ...prev]);
  
      // ✅ success toast
      toast.success("Transaction added");
    } catch (err) {
      console.error("Failed to add transaction", err);
      toast.error("Failed to add transaction");
    } finally {
      setLoading(false);
    }
  };

  // Delete transaction via backend
  const deleteTransaction = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
  
      toast.success("Transaction deleted");
    } catch (err) {
      console.error("Failed to delete transaction", err);
      toast.error("Failed to delete transaction");
    }
  };
  // Totals
  const totals = useMemo(() => {
    const income = transactions
      .filter((t) => t.type === "income")
      .reduce((a, b) => a + b.amount, 0);
    const expenses = transactions
      .filter((t) => t.type === "expense")
      .reduce((a, b) => a + b.amount, 0);
    const balance = income - expenses;
    return { income, expenses, balance };
  }, [transactions]);

  // Category totals for chart
  const categoryTotals = useMemo(() => {
    const map = {};
    transactions
      .filter((t) => t.type === "expense")
      .forEach((t) => {
        map[t.category] = (map[t.category] || 0) + t.amount;
      });
    return Object.entries(map).map(([name, value]) => ({
      name,
      value: Number(value.toFixed(2)),
    }));
  }, [transactions]);

  // Modal controls
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // 🌟 Smart notifications
  useEffect(() => {
    if (transactions.length === 0) return;
  
    if (hasShownRef.current) return; // ✅ prevents repeat
    hasShownRef.current = true;

    const now = new Date();
    const totalIncome = totals.income;
    const totalExpenses = totals.expenses;
    const balance = totals.balance;
    const hour = now.getHours();
    const possibleNotifications = [];

    if (hour >= 12 && hour < 15) {
      possibleNotifications.push("Don’t forget to log today’s lunch 🍔");
    }
    if (hour >= 19 && hour < 22) {
      possibleNotifications.push(
        "It’s dinner time 🌙 — did you record your evening spend yet?"
      );
    }
    if (totalExpenses > totalIncome * 0.8) {
      possibleNotifications.push(
        "You're close to exceeding your budget ⚠️. Keep an eye on spending!"
      );
    }
    const shopping = transactions
      .filter((t) => t.category?.toLowerCase().includes("shopping"))
      .reduce((sum, t) => sum + t.amount, 0);
    if (shopping > totalIncome * 0.3) {
      possibleNotifications.push(
        "Your shopping expenses seem high 🛒. Maybe skip that extra cart today?"
      );
    }
    if (balance > 0 && totalExpenses < totalIncome * 0.5) {
      possibleNotifications.push(
        "You’re doing great at saving this week 💰. Keep it up!"
      );
    }

    possibleNotifications.push(
      "Small steps make big savings 🚀. Stay consistent today!"
    );
    possibleNotifications.push(
      "Check your weekly budget 💳 — know where your money is going!"
    );
    if ([1, 3, 5].includes(now.getDay())) {
      possibleNotifications.push(
        "Midweek check-in 🔄 — how’s your budget progress going?"
      );
    }

    const lastTxDate = transactions[0]?.date
      ? new Date(transactions[0].date)
      : null;
    if (lastTxDate) {
      const daysSince = Math.floor(
        (now - lastTxDate) / (1000 * 60 * 60 * 24)
      );
      if (daysSince >= 2) {
        possibleNotifications.push(
          "You haven’t logged any expenses in a while 🕒. Keep your tracker up to date!"
        );
      }
    }

    possibleNotifications.push(
      "Tip: Even skipping one coffee a week ☕ can add to your savings!"
    );

    const shuffle = (arr) => arr.sort(() => 0.5 - Math.random());
    const selected = shuffle(possibleNotifications).slice(0, 2);
  
    selected.forEach((msg, i) => {
      setTimeout(() => toast.info(msg), 2000 + i * 2000);
    });
  
  }, [transactions, totals]);

  return (
    <div className="layout">
      <Sidebar sidebarOpen={sidebarOpen} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      <div className="main">
        <Navbar toggleSidebar={toggleSidebar} />
        <div className="content">
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard
                  totals={totals}
                  transactions={transactions}
                  deleteTransaction={deleteTransaction}
                  categoryData={categoryTotals}
                  openModal={openModal}
                />
              }
            />
            <Route path="/add" element={<AddTransaction onAdd={addTransaction} loading={loading} />} />
            <Route path="/receipts" element={<ReceiptsPage />} /> {/* ✅ Receipts route */}
            <Route path="/saved-cards" element={<SavedCards />} />
            <Route path="/test" element={<BackendTest />} />
          </Routes>
        </div>
      </div>

      {/* Floating AI Chatbot button only on dashboard */}
      {location.pathname === "/" && (
        <>
          <button
            className="fab"
            onClick={() => setChatOpen((prev) => !prev)}
            title="Chat with AI Assistant"
          >
            💬
          </button>

          {chatOpen && (
            <div className="chatbot-popup">
              <div className="chatbot-header">
                <span>AI Finance Assistant</span>
                <button
                  className="close-btn"
                  onClick={() => setChatOpen(false)}
                >
                  ✖
                </button>
              </div>

              <div className="chatbot-body">
                {messages.map((msg, i) => (
                  <div key={i} className={`chat-msg ${msg.sender}`}>
                    {msg.text}
                  </div>
                ))}
              </div>

              <div className="chatbot-input">
                <input
                  type="text"
                  placeholder="Ask about income, expenses, or advice..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                />
                <button onClick={handleSendMessage}>➤</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Transaction Modal */}
      <AddTransactionModal
      isOpen={isModalOpen}
      onClose={closeModal}
      onSubmit={async (data) => {
        await addTransaction(data);
        closeModal();
      }}
      loading={loading}
    />

      {/* Toastify Container */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
